import React, { useState } from "react";
import Styles from "./AddContractModal.module.css";
import { Modal } from "@mui/material";
import { Close, Add, Delete, FileUpload } from "@mui/icons-material";
import DateInput from "../../commons/Inputs/DateInput/DateInput";
import TimeInput from "../../commons/Inputs/TimeInput/TimeInput";
import TextInput from "../../commons/Inputs/TextInput/TextInput";
import SelectInput from "../../commons/Inputs/Select/SelectInput";
import {
  extractPdf,
  getExtractPdfStatus,
} from "../../Services/banking/BankingCalc";

const BASIS_OPTIONS = ["As & When", "Firm", "Daily", "Weekly"];
const MAX_QUANTUM_OPTIONS = ["Exact", "Up to"];
const TRADER_OPTIONS = ["NEWAGE", "MANIKARAN", "IEXL", "PTC", "STCI"];

// Poll every 2s, up to 30 attempts (60s max)
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30;

const defaultSupplyPeriod = {
  startDate: null,
  endDate: null,
  quantum: "",
  maxQuantum: "Exact",
  startTime: "",
  endTime: "",
  basis: "As & When",
  basisText: "",
};

const defaultReturnPeriod = {
  startDate: null,
  endDate: null,
  bankingFactor: "",
  quantum: "",
  maxQuantum: "Exact",
  startTime: "",
  endTime: "",
  basis: "As & When",
  basisText: "",
};

const AddContractModal = ({ open, handleClose }) => {
  const [form, setForm] = useState({
    loaNumber: "",
    loaDate: null,
    supplier: "",
    receiver: "",
    notes: "",
    trader: "",
  });
  const [supplyPeriods, setSupplyPeriods] = useState([
    { ...defaultSupplyPeriod },
  ]);
  const [returnPeriods, setReturnPeriods] = useState([
    { ...defaultReturnPeriod },
  ]);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState("");

  const updateForm = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const updatePeriod = (setter, idx, key, val) =>
    setter((p) => {
      const n = [...p];
      n[idx] = { ...n[idx], [key]: val };
      return n;
    });

  // Parse a "YYYY-MM-DD" string into a Date, leaving null/empty as null
  const parseDate = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  // Map a snake_case period object from the API into the form's camelCase shape
  const mapPeriod = (p, fallback) => ({
    ...fallback,
    startDate: parseDate(p.start_date ?? p.startDate) ?? fallback.startDate,
    endDate: parseDate(p.end_date ?? p.endDate) ?? fallback.endDate,
    quantum: p.quantum ?? fallback.quantum,
    maxQuantum: p.max_quantum ?? p.maxQuantum ?? fallback.maxQuantum,
    startTime: p.start_time ?? p.startTime ?? fallback.startTime,
    endTime: p.end_time ?? p.endTime ?? fallback.endTime,
    basis: p.basis ?? fallback.basis,
    basisText: p.basis_text ?? p.basisText ?? fallback.basisText,
    ...(fallback.bankingFactor !== undefined
      ? { bankingFactor: p.banking_factor ?? p.bankingFactor ?? fallback.bankingFactor }
      : {}),
  });

  // Map extracted API fields → form + period state
  const prefillFromExtracted = (data) => {
    setForm((prev) => ({
      ...prev,
      loaNumber: data.loa_number ?? data.loaNumber ?? "",
      loaDate: parseDate(data.loa_date ?? data.loaDate),
      supplier: data.supplier ?? "",
      receiver: data.receiver ?? "",
      notes: data.notes ?? "",
      trader: TRADER_OPTIONS.includes(data.trader ?? "") ? data.trader : "",
    }));

    const supply = data.supply_periods ?? data.supplyPeriods;
    if (Array.isArray(supply) && supply.length) {
      setSupplyPeriods(supply.map((p) => mapPeriod(p, defaultSupplyPeriod)));
    }

    const ret = data.return_periods ?? data.returnPeriods;
    if (Array.isArray(ret) && ret.length) {
      setReturnPeriods(ret.map((p) => mapPeriod(p, defaultReturnPeriod)));
    }
  };

  const pollForResult = async (taskId, attempts = 0) => {
    if (attempts >= MAX_POLL_ATTEMPTS) {
      setParseError("Extraction timed out. Please fill in manually.");
      setIsParsing(false);
      return;
    }

    try {
      const res = await getExtractPdfStatus(taskId);
      const body = res?.data ?? res;

      if (body?.status === "completed" || body?.status === "success") {
        prefillFromExtracted(body.data ?? body.result ?? body);
        setIsParsing(false);
      } else if (body?.status === "failed" || body?.status === "error") {
        setParseError("Extraction failed. Please fill in manually.");
        setIsParsing(false);
      } else {
        // Still processing — poll again
        setTimeout(() => pollForResult(taskId, attempts + 1), POLL_INTERVAL_MS);
      }
    } catch (err) {
      setParseError(
        "Could not retrieve extraction result. Please fill in manually.",
      );
      setIsParsing(false);
    }
  };

  const parseAndPrefill = async (file) => {
    setIsParsing(true);
    setParseError("");
    setUploadedFileName(file.name);

    try {
      // POST multipart/form-data with key "file"
      const res = await extractPdf(file);
      const body = res?.data ?? res;
      const taskId = body?.task_id ?? body?.taskId ?? body?.id;

      if (!taskId) throw new Error("No task ID returned from extract-pdf");

      // Begin polling for the extraction result
      setTimeout(() => pollForResult(taskId), POLL_INTERVAL_MS);
    } catch (err) {
      setParseError("Could not start extraction. Please fill in manually.");
      setIsParsing(false);
    }
  };

  const handleReset = () => {
    setForm({
      loaNumber: "",
      loaDate: null,
      supplier: "",
      receiver: "",
      notes: "",
      trader: "",
    });
    setSupplyPeriods([{ ...defaultSupplyPeriod }]);
    setReturnPeriods([{ ...defaultReturnPeriod }]);
    setUploadedFileName("");
    setParseError("");
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleReset}>
      <div className={Styles.modalWrap}>
        {/* ── Header ── */}
        <div className={Styles.header}>
          <span className={Styles.headerTitle}>Contract Details</span>
          <div className={Styles.headerRight}>
            {uploadedFileName && (
              <span className={Styles.uploadedChip}>{uploadedFileName}</span>
            )}
            {isParsing && (
              <span className={Styles.parsingIndicator}>Extracting data…</span>
            )}
            <label
              className={`activeButton_type_2 ${Styles.templateBtn} ${isParsing ? Styles.disabled : ""}`}
              htmlFor="contract-pdf-upload"
              style={isParsing ? { pointerEvents: "none", opacity: 0.6 } : {}}
            >
              <FileUpload style={{ fontSize: "15px" }} />
              Upload LOA
            </label>
            <input
              id="contract-pdf-upload"
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              disabled={isParsing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) parseAndPrefill(f);
                // Reset input so the same file can be re-uploaded
                e.target.value = "";
              }}
            />
            <Close className={Styles.closeIcon} onClick={handleReset} />
          </div>
        </div>

        {/* ── Parse error banner ── */}
        {parseError && <div className={Styles.errorBanner}>{parseError}</div>}

        {/* ── Body ── */}
        <div className={`${Styles.body} scroll_bar_y`}>
          <div className={Styles.formSection}>
            <div className={Styles.formGrid}>
              <TextInput
                heading="LOA Number"
                example="E.g. 252603032"
                value={form.loaNumber}
                onChange={(v) => updateForm("loaNumber", v)}
                height={42}
              />
              <DateInput
                heading="LOA Date"
                value={form.loaDate}
                onChange={(v) => updateForm("loaDate", v)}
                height={42}
              />
              <TextInput
                heading="Supplier"
                example="E.g. MPPMCL"
                value={form.supplier}
                onChange={(v) => updateForm("supplier", v)}
                height={42}
              />
              <TextInput
                heading="Receiver"
                example="E.g. CSPDCL"
                value={form.receiver}
                onChange={(v) => updateForm("receiver", v)}
                height={42}
              />
              <div style={{ gridColumn: "span 2" }}>
                <TextInput
                  heading="Notes"
                  example="Special Conditions..."
                  value={form.notes}
                  onChange={(v) => updateForm("notes", v)}
                  height={42}
                />
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <SelectInput
                  heading="Trader Name"
                  example="Select traders"
                  list={TRADER_OPTIONS}
                  value={form.trader}
                  onChange={(v) => updateForm("trader", v)}
                  height={42}
                />
              </div>
            </div>
          </div>

          <PeriodSection
            title="Supply Periods"
            periods={supplyPeriods}
            onAdd={() =>
              setSupplyPeriods((p) => [...p, { ...defaultSupplyPeriod }])
            }
            onRemove={(i) =>
              setSupplyPeriods((p) => p.filter((_, idx) => idx !== i))
            }
            onUpdate={(i, k, v) => updatePeriod(setSupplyPeriods, i, k, v)}
            showBankingFactor={false}
          />

          <PeriodSection
            title="Return Period"
            periods={returnPeriods}
            onAdd={() =>
              setReturnPeriods((p) => [...p, { ...defaultReturnPeriod }])
            }
            onRemove={(i) =>
              setReturnPeriods((p) => p.filter((_, idx) => idx !== i))
            }
            onUpdate={(i, k, v) => updatePeriod(setReturnPeriods, i, k, v)}
            showBankingFactor
          />
        </div>

        {/* ── Footer ── */}
        <div className={Styles.footer}>
          <button
            className={`activeButton_type_3 ${Styles.footerBtn}`}
            onClick={handleReset}
          >
            Cancel
          </button>
          <button
            className={`activeButton ${Styles.footerBtn} ${Styles.saveBtn}`}
          >
            Save Contract
          </button>
        </div>
      </div>
    </Modal>
  );
};

// PeriodSection unchanged
const PeriodSection = ({
  title,
  periods,
  onAdd,
  onRemove,
  onUpdate,
  showBankingFactor,
}) => (
  <div className={Styles.periodSection}>
    <div className={Styles.periodSectionHeader}>
      <span className={Styles.sectionTitle}>{title}</span>
      <button
        className={`activeButton_type_2 ${Styles.addPeriodBtn}`}
        onClick={onAdd}
      >
        <Add style={{ fontSize: "13px" }} /> Add Period
      </button>
    </div>
    {periods.map((p, i) => {
      const cols = showBankingFactor ? 7 : 6;
      return (
        <div key={i} className={Styles.periodCard}>
          {periods.length > 1 && (
            <div className={Styles.periodCardHeader}>
              <span className={Styles.periodLabel}>Period {i + 1}</span>
              <button className={Styles.deleteBtn} onClick={() => onRemove(i)}>
                <Delete style={{ fontSize: "14px" }} />
              </button>
            </div>
          )}
          <div
            className={Styles.periodRow}
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            <DateInput
              heading="Start Date"
              value={p.startDate}
              onChange={(v) => onUpdate(i, "startDate", v)}
              height={42}
            />
            <DateInput
              heading="End Date"
              value={p.endDate}
              onChange={(v) => onUpdate(i, "endDate", v)}
              height={42}
            />
            {showBankingFactor && (
              <TextInput
                heading="Banking Factor"
                example="E.g. 105%"
                value={p.bankingFactor}
                onChange={(v) => onUpdate(i, "bankingFactor", v)}
                height={42}
              />
            )}
            <TextInput
              heading="Quantum"
              example="E.g. 300 Mw"
              value={p.quantum}
              onChange={(v) => onUpdate(i, "quantum", v)}
              height={42}
            />
            <SelectInput
              heading="Max Quantum"
              example="Exact"
              list={MAX_QUANTUM_OPTIONS}
              value={p.maxQuantum}
              onChange={(v) => onUpdate(i, "maxQuantum", v)}
              height={42}
            />
            <TimeInput
              heading="Start Time"
              value={p.startTime}
              onChange={(v) => onUpdate(i, "startTime", v)}
              height={42}
            />
            <TimeInput
              heading="End Time"
              value={p.endTime}
              onChange={(v) => onUpdate(i, "endTime", v)}
              height={42}
              type="endTime"
              minTime={p.startTime}
            />
          </div>
          <div className={Styles.basisRow}>
            <div style={{ flex: "0 0 200px" }}>
              <SelectInput
                heading="Basis"
                example="As & When"
                list={BASIS_OPTIONS}
                value={p.basis}
                onChange={(v) => onUpdate(i, "basis", v)}
                height={40}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextInput
                heading="Basis Text Raw (Raw)"
                example="Special Conditions..."
                value={p.basisText}
                onChange={(v) => onUpdate(i, "basisText", v)}
                height={40}
              />
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default AddContractModal;
